import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { EligibilityListItem, EligibilityOption, EligibilityRequest } from './eligibilityTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check here would drop it and
    the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const eligibilitiesApi = {
  /** Soft-deletes every eligibility at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/eligibilities/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<EligibilityListItem>>(`/api/eligibilities${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<EligibilityListItem>(`/api/eligibilities/${id}`, { signal }),

  /** Active eligibilities for the position form's picker. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<EligibilityOption[]>('/api/eligibilities/options', { signal }),

  create: (request: EligibilityRequest) =>
    api<EligibilityListItem>('/api/eligibilities', { method: 'POST', body: request }),

  update: (id: string, request: EligibilityRequest) =>
    api<EligibilityListItem>(`/api/eligibilities/${id}`, { method: 'PUT', body: request }),

  /** Soft delete. Titles requiring it keep resolving its name. */
  deactivate: (id: string) => api<void>(`/api/eligibilities/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/eligibilities/${id}/restore`, { method: 'POST' }),
};
